import { useEffect, useMemo, useState } from "react";
import { useIdVerification } from "@/hooks/useIdVerification";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { Button } from "@/components/ui/button";
import { Lightbulb, X } from "lucide-react";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

const storageKeyForUser = (userId: string) =>
  `bilenia.bankIdTip.dismissed.${userId}`;

/**
 * Soft tip for users who already passed Didit/KYC but have not completed BankID.
 * Encourages BankID for future login + contract signing.
 * Dismissible; preference stored in localStorage per user.
 */
export const BankIdComplementBanner = () => {
  const { user } = useAuth();
  const { kycStatus, bankIdStatus, startVerification, isStarting } =
    useIdVerification();
  const labels = useMemo(() => authLabels(getLanguage()), []);
  const tip = labels.kyc?.bankIdTip ?? {};
  const [dismissed, setDismissed] = useState(false);

  const isDiditApproved = kycStatus === "approved";
  const isBankIdApproved = bankIdStatus === "approved";
  const isBankIdPending = bankIdStatus === "pending";

  useEffect(() => {
    if (!user?.id) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(localStorage.getItem(storageKeyForUser(user.id)) === "1");
    } catch {
      setDismissed(false);
    }
  }, [user?.id]);

  const handleDismiss = () => {
    if (!user?.id) return;
    try {
      localStorage.setItem(storageKeyForUser(user.id), "1");
    } catch {
      // ignore quota / private mode
    }
    setDismissed(true);
  };

  if (
    !user ||
    user.type === UserType.Admin ||
    !isDiditApproved ||
    isBankIdApproved ||
    dismissed
  ) {
    return null;
  }

  const ctaLabel = isStarting
    ? tip.pending || "BankID-verifiering pågår..."
    : isBankIdPending
      ? tip.restart || "Öppna BankID igen"
      : tip.cta || "Verifiera med BankID";

  return (
    <div className="relative w-full md:w-[85%] md:mx-auto mb-4 rounded-xl border border-teal-600/25 bg-gradient-to-r from-teal-50/90 via-white to-slate-50/80 dark:from-teal-950/40 dark:via-background dark:to-slate-950/30 shadow-sm">
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-2 top-2 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        aria-label={tip.dismissAria || "Stäng tips"}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 pr-10 sm:pr-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-600/10 text-teal-700 dark:text-teal-300">
            <Lightbulb className="h-4 w-4" />
          </div>
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-teal-600/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200">
                {tip.badge || "Tips"}
              </span>
              <p className="text-sm font-semibold text-foreground">
                {tip.title || "Verifiera med BankID"}
              </p>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              {tip.description ||
                "Autentisera din identitet med BankID så kan du i framtiden logga in med BankID och signera eventuella kontrakt med BankID-signering."}
            </p>
            {isBankIdPending && (
              <p className="text-xs text-teal-800/80 dark:text-teal-200/80">
                {tip.pending || "BankID-verifiering pågår..."}
              </p>
            )}
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={isStarting}
          onClick={() =>
            startVerification("bankid", { forceRestart: isBankIdPending })
          }
          className="shrink-0 gap-2 bg-teal-700 hover:bg-teal-600 text-white self-stretch sm:self-center"
        >
          <img
            src="/assets/BankID_logo.png"
            alt=""
            className="h-4 w-auto object-contain brightness-0 invert"
          />
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};
